import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation } from '@tanstack/react-query';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { z } from 'zod';
import queryClient from '@/Config/api/queryClient';
import { QueryKey } from '@/Config/api/queryKey';
import { RemoteMcpDto } from '@/Domain/api/Action/dto/RemoteMCP.dto';
import { useModalStore } from '@/Domain/store/modal.store';

const remoteMcpActionSchema = (isActive: boolean) => {
  if (isActive) {
    return z.object({
      name: z.string().min(1),
      default_tool_is_active: z.boolean(),
      allowed_tools: z.array(
        z.object({
          name: z.string(),
          is_active: z.boolean(),
        }),
      ),
    });
  }
  return z.object({
    name: z.string(),
    default_tool_is_active: z.boolean(),
    allowed_tools: z.array(
      z.object({
        name: z.string(),
        is_active: z.boolean(),
      }),
    ),
  });
};

export type RemoteMcpForm = z.infer<ReturnType<typeof remoteMcpActionSchema>>;

export const useRemoteMcpActionForm = (remoteMcpAction?: RemoteMcpDto) => {
  const { t } = useTranslation('remote_mcp');
  const { open, closeModal } = useModalStore();

  useEffect(() => {
    if (!remoteMcpAction) return;
  }, [remoteMcpAction]);

  const defaultPatchRemoteMcpMutationOptions = queryClient.getMutationDefaults([
    QueryKey.actions.remoteMcp.update,
  ]);
  const patchRemoteMcpMutation = useMutation<RemoteMcpDto, unknown, Partial<RemoteMcpDto>>({
    mutationKey: [QueryKey.actions.remoteMcp.update, remoteMcpAction?.id],
    ...defaultPatchRemoteMcpMutationOptions,
    onSuccess: (data, variables, context) => {
      defaultPatchRemoteMcpMutationOptions?.onSuccess?.(data, variables, context);
      closeModal();
    },
  });

  const form = useForm<RemoteMcpForm>({
    mode: 'onChange',
    defaultValues: {
      name: remoteMcpAction?.name || '',
      allowed_tools: remoteMcpAction?.allowed_tools || [],
    },
    resolver: zodResolver(remoteMcpActionSchema(!!remoteMcpAction?.is_active)),
  });

  const handleSubmit = async (data: RemoteMcpForm) => {
    if (!remoteMcpAction) return;
    const updateData = {
      id: remoteMcpAction.id,
      name: data.name,
      default_tool_is_active: data.default_tool_is_active,
      allowed_tools: data.allowed_tools,
    };
    patchRemoteMcpMutation.mutate(updateData);
  };

  useEffect(() => {
    if (remoteMcpAction) {
      form.reset({
        name: remoteMcpAction.name,
        default_tool_is_active: remoteMcpAction.default_tool_is_active,
        allowed_tools: remoteMcpAction.allowed_tools,
      });

      return;
    }

    form.reset({
      name: '',
      default_tool_is_active: true,
      allowed_tools: [],
    });
  }, [open, remoteMcpAction]);

  const onInvalid = () => {
    toast.error(t('toast.invalid_update_on_is_active'));
  };

  return { form, handleSubmit, onInvalid };
};
